import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { req } from './api';

export interface AdminEvent {
  id: number; slug: string; title: string;
  starts_at: number; ends_at: number; timezone: string;
  venue_id: number; currency: string;
  capacity: number | null; kids_welcome: 0 | 1 | boolean;
  blurb: string; description: string;
  hero_image_url: string | null; ticket_url: string | null;
  organiser_name: string | null; pull_quote: string | null;
  expect_heading: string | null; tags: string[];
  status: 'draft' | 'published' | 'cancelled';
  published_at: number | null; created_at: number; updated_at: number;
}
export interface EventHighlightRow {
  id: number; event_id: number; title: string; body: string;
  enabled: boolean; sort_order: number;
  created_at: number; updated_at: number;
}
export interface Venue {
  id: number; name: string; address_line: string; town: string;
  region_id: number; postcode: string | null; lat: number | null; lng: number | null;
}
export interface Region { id: number; slug: string; name: string; sort_order: number }
export interface TicketTypeRow {
  id: number; event_id: number; name: string; description: string | null;
  price_pence: number; currency: 'GBP'; capacity: number | null;
  sales_start_at: number | null; sales_end_at: number | null;
  visibility: 'public' | 'members' | 'hidden';
  max_per_order: number; sort_order: number;
  created_at: number; updated_at: number;
}
export interface Subscriber {
  id: number; email: string; first_name: string | null; brevo_contact_id: string | null;
  source: string; status: 'active' | 'unsubscribed' | 'bounced' | 'complained';
  subscribed_at: number; updated_at: number; last_synced_at: number | null;
}
export interface NewsletterIssue {
  id: number; slug: string; title: string; preheader: string | null; body_md: string;
  scheduled_for: number | null; sent_at: number | null; brevo_campaign_id: string | null;
  status: 'draft' | 'scheduled' | 'sent' | 'failed';
  recipient_count: number | null; created_at: number; updated_at: number;
}
export interface AuditRow {
  id: number; actor_email: string; action: string; entity: string;
  entity_id: string | null; payload: string | null; created_at: number;
}
export interface OutboxRow {
  id: number; to_email: string; template: string; payload: string;
  status: 'pending' | 'sending' | 'sent' | 'failed' | 'abandoned';
  attempts: number; last_error: string | null;
  scheduled_for: number; claimed_at: number | null; sent_at: number | null;
  provider_message_id: string | null; idempotency_key: string | null;
  created_at: number; updated_at: number;
}

function normalizeEvent(row: any): AdminEvent {
  return {
    id: row.id, slug: row.slug, title: row.title,
    starts_at: row.startsAt ?? row.starts_at,
    ends_at: row.endsAt ?? row.ends_at,
    timezone: row.timezone,
    venue_id: row.venueId ?? row.venue_id,
    currency: row.currency,
    capacity: row.capacity,
    kids_welcome: (row.kidsWelcome ?? row.kids_welcome) ? 1 : 0,
    blurb: row.blurb, description: row.description,
    hero_image_url: row.heroImageUrl ?? row.hero_image_url,
    ticket_url: row.ticketUrl ?? row.ticket_url,
    organiser_name: row.organiserName ?? row.organiser_name ?? null,
    pull_quote: row.pullQuote ?? row.pull_quote ?? null,
    expect_heading: row.expectHeading ?? row.expect_heading ?? null,
    // The admin endpoints return raw drizzle rows, where a JSON column arrives
    // already parsed but is null on any event written before the column existed.
    tags: row.tags ?? [],
    status: row.status,
    published_at: row.publishedAt ?? row.published_at,
    created_at: row.createdAt ?? row.created_at,
    updated_at: row.updatedAt ?? row.updated_at,
  };
}

function normalizeVenue(row: any): Venue {
  return {
    id: row.id, name: row.name,
    address_line: row.addressLine ?? row.address_line,
    town: row.town,
    region_id: row.regionId ?? row.region_id,
    postcode: row.postcode, lat: row.lat, lng: row.lng,
  };
}

function normalizeRegion(row: any): Region {
  return {
    id: row.id, slug: row.slug, name: row.name,
    sort_order: row.sortOrder ?? row.sort_order ?? 0,
  };
}

function normalizeSubscriber(row: any): Subscriber {
  return {
    id: row.id, email: row.email,
    first_name: row.firstName ?? row.first_name,
    brevo_contact_id: row.brevoContactId ?? row.brevo_contact_id,
    source: row.source, status: row.status,
    subscribed_at: row.subscribedAt ?? row.subscribed_at,
    updated_at: row.updatedAt ?? row.updated_at,
    last_synced_at: row.lastSyncedAt ?? row.last_synced_at,
  };
}

function normalizeNewsletter(row: any): NewsletterIssue {
  return {
    id: row.id, slug: row.slug, title: row.title,
    preheader: row.preheader,
    body_md: row.bodyMd ?? row.body_md,
    scheduled_for: row.scheduledFor ?? row.scheduled_for,
    sent_at: row.sentAt ?? row.sent_at,
    brevo_campaign_id: row.brevoCampaignId ?? row.brevo_campaign_id,
    status: row.status,
    recipient_count: row.recipientCount ?? row.recipient_count,
    created_at: row.createdAt ?? row.created_at,
    updated_at: row.updatedAt ?? row.updated_at,
  };
}

function normalizeAudit(row: any): AuditRow {
  return {
    id: row.id, actor_email: row.actorEmail ?? row.actor_email,
    action: row.action, entity: row.entity,
    entity_id: row.entityId ?? row.entity_id,
    payload: row.payload,
    created_at: row.createdAt ?? row.created_at,
  };
}

function normalizeOutbox(row: any): OutboxRow {
  return {
    id: row.id,
    to_email: row.toEmail ?? row.to_email,
    template: row.template,
    payload: row.payload,
    status: row.status,
    attempts: row.attempts,
    last_error: row.lastError ?? row.last_error,
    scheduled_for: row.scheduledFor ?? row.scheduled_for,
    claimed_at: row.claimedAt ?? row.claimed_at,
    sent_at: row.sentAt ?? row.sent_at,
    provider_message_id: row.providerMessageId ?? row.provider_message_id,
    idempotency_key: row.idempotencyKey ?? row.idempotency_key,
    created_at: row.createdAt ?? row.created_at,
    updated_at: row.updatedAt ?? row.updated_at,
  };
}

export type Role = 'owner' | 'administrator' | 'head_leader' | 'networking_leader';

export interface Me {
  person_id: number;
  email: string;
  acting_role: Role;
  can_view_finances: boolean;
  scope: 'global' | { region_ids: number[] };
  can_switch_role: boolean;
  assignments: { role: Role; region_id: number | null; region_name: string | null; can_view_finances: boolean }[];
}

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => req<Me>('/admin/me'),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Switching hats changes what every other endpoint returns, so the whole cache
 * is dropped rather than trying to enumerate what became stale.
 */
export function useSwitchRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { role: Role; region_id?: number | null }) =>
      req<{ acting_role: Role }>('/admin/me/acting-role', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.clear(),
  });
}

export function useEvents(params: { status?: string } = {}) {
  return useQuery({
    queryKey: ['events', params],
    queryFn: async () => {
      const qs = params.status ? `?status=${params.status}` : '';
      const res = await req<{ data: any[] }>(`/admin/events${qs}`);
      return res.data.map(normalizeEvent);
    },
  });
}

export function useEvent(id: number) {
  return useQuery({
    queryKey: ['event', id],
    queryFn: async () => normalizeEvent(await req<any>(`/admin/events/${id}`)),
    enabled: Number.isFinite(id),
  });
}

export function useTicketTypes(eventId: number) {
  return useQuery({
    queryKey: ['ticket-types', eventId],
    // The API already serialises snake_case here, so no normaliser is needed.
    queryFn: async () => (await req<{ data: TicketTypeRow[] }>(`/admin/events/${eventId}/ticket-types`)).data,
    enabled: Number.isFinite(eventId),
  });
}

export function useEventHighlights(eventId: number) {
  return useQuery({
    queryKey: ['event-highlights', eventId],
    // Serialised snake_case by the API, so no normaliser is needed.
    queryFn: async () => (await req<{ data: EventHighlightRow[] }>(`/admin/events/${eventId}/highlights`)).data,
    enabled: Number.isFinite(eventId),
  });
}

export interface BusinessRow {
  id: number; slug: string; name: string;
  owner_name: string; owner_email: string | null; person_id: number;
  category_id: number | null; category: string | null;
  tagline: string | null; pitch: string | null; about: string | null;
  country: string; region: string | null; town: string | null;
  website_url: string | null; contact_email: string | null; contact_phone: string | null;
  works_worldwide: boolean; mib_group: string | null;
  owner_role: string | null; owner_bio: string | null;
  tier: 'standard' | 'featured' | 'spotlight';
  status: 'draft' | 'published' | 'suspended';
  member_since: number | null; created_at: number; updated_at: number;
}

export interface BusinessCategoryRow {
  id: number; slug: string; name: string; sort_order: number; listing_count: number;
}

export function useBusinesses(params: { status?: string; tier?: string } = {}) {
  return useQuery({
    queryKey: ['businesses', params],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.tier) qs.set('tier', params.tier);
      const s = qs.toString();
      return (await req<{ data: BusinessRow[] }>(`/admin/businesses${s ? `?${s}` : ''}`)).data;
    },
  });
}

export function useBusiness(id: number) {
  return useQuery({
    queryKey: ['business', id],
    queryFn: () => req<BusinessRow>(`/admin/businesses/${id}`),
    enabled: Number.isFinite(id),
  });
}

export function useBusinessStats(id: number) {
  return useQuery({
    queryKey: ['business', id, 'stats'],
    queryFn: () => req<{
      totals: Record<string, number>;
      weekly: Array<{ label: string; value: number }>;
      top_terms: Array<{ term: string; count: number }>;
    }>(`/admin/businesses/${id}/stats`),
    enabled: Number.isFinite(id),
  });
}

export function useBusinessCategories() {
  return useQuery({
    queryKey: ['business-categories'],
    queryFn: async () => (await req<{ data: BusinessCategoryRow[] }>('/admin/business-categories')).data,
  });
}

export interface OrderRow {
  id: number; reference: string;
  status: 'pending' | 'paid' | 'cancelled' | 'refunded' | 'partially_refunded';
  total_pence: number; refunded_pence: number; currency: string;
  buyer_email: string; buyer_name: string | null;
  paid_at: number | null; created_at: number;
  event_id: number; event_title: string; event_slug: string; starts_at: number;
}

export interface OrderDetail {
  reference: string; status: OrderRow['status'];
  total_pence: number; refunded_pence: number; currency: string;
  buyer_email: string; buyer_name: string | null;
  stripe_payment_intent_id: string | null;
  hold_expires_at: number | null; paid_at: number | null;
  cancelled_at: number | null; created_at: number;
  event: { id: number; slug: string; title: string; starts_at: number };
  items: Array<{ name: string; unit_price_pence: number; quantity: number; subtotal_pence: number }>;
  tickets: Array<{ code: string; status: string; checked_in_at: number | null; ticket_type: string }>;
}

export interface Attendee {
  code: string; status: string; checked_in_at: number | null;
  ticket_type: string; order_reference: string;
  buyer_email: string; buyer_name: string | null;
}

export interface DoorList {
  event: { id: number; title: string; slug: string; starts_at: number };
  totals: { total: number; checked_in: number };
  data: Attendee[];
}

export function useOrders(params: { status?: string; event_id?: number; q?: string } = {}) {
  return useQuery({
    queryKey: ['orders', params],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.event_id) qs.set('event_id', String(params.event_id));
      if (params.q) qs.set('q', params.q);
      const s = qs.toString();
      return (await req<{ data: OrderRow[] }>(`/admin/orders${s ? `?${s}` : ''}`)).data;
    },
  });
}

export function useOrder(reference: string) {
  return useQuery({
    queryKey: ['order', reference],
    queryFn: () => req<OrderDetail>(`/admin/orders/${encodeURIComponent(reference)}`),
    enabled: !!reference,
  });
}

export function useDoorList(eventId: number, q?: string) {
  return useQuery({
    queryKey: ['door', eventId, q],
    queryFn: () => req<DoorList>(`/admin/events/${eventId}/attendees${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    enabled: Number.isFinite(eventId),
    // The door is often worked from more than one phone at once.
    refetchInterval: 10000,
  });
}

export interface MemberRow {
  id: number; email: string;
  display_name: string | null; first_name: string | null; last_name: string | null;
  town: string | null; business_name: string | null;
  status: 'active' | 'suspended';
  directory_opt_in: boolean; directory_opt_in_at: number | null;
  email_verified_at: number | null; last_login_at: number | null; created_at: number;
}

export interface MemberDetail extends MemberRow {
  bio: string | null; business_url: string | null; avatar_url: string | null;
  active_sessions: number;
  orders: Array<{ reference: string; status: string; total_pence: number; created_at: number }>;
}

export interface ResourceRow {
  id: number; slug: string; title: string;
  summary: string | null; body_md: string | null; link_url: string | null;
  visibility: 'public' | 'members';
  status: 'draft' | 'published';
  sort_order: number; created_at: number; updated_at: number;
}

export function useMembers(params: { status?: string; q?: string; directory?: boolean } = {}) {
  return useQuery({
    queryKey: ['members', params],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.q) qs.set('q', params.q);
      if (params.directory) qs.set('directory', '1');
      const s = qs.toString();
      return (await req<{ data: MemberRow[] }>(`/admin/members${s ? `?${s}` : ''}`)).data;
    },
  });
}

export function useMember(id: number) {
  return useQuery({
    queryKey: ['member', id],
    queryFn: () => req<MemberDetail>(`/admin/members/${id}`),
    enabled: Number.isFinite(id),
  });
}

export function useResources() {
  return useQuery({
    queryKey: ['resources'],
    queryFn: async () => (await req<{ data: ResourceRow[] }>('/admin/resources')).data,
  });
}

export function useVenues() {
  return useQuery({
    queryKey: ['venues'],
    queryFn: async () => (await req<{ data: any[] }>('/admin/venues')).data.map(normalizeVenue),
  });
}

export function useRegions() {
  return useQuery({
    queryKey: ['regions'],
    queryFn: async () => (await req<{ data: any[] }>('/admin/regions')).data.map(normalizeRegion),
  });
}

export function useSubscribers(params: { status?: string; q?: string; limit?: number } = {}) {
  return useQuery({
    queryKey: ['subscribers', params],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.q) qs.set('q', params.q);
      if (params.limit) qs.set('limit', String(params.limit));
      const s = qs.toString();
      const res = await req<{ data: any[] }>(`/admin/subscribers${s ? `?${s}` : ''}`);
      return res.data.map(normalizeSubscriber);
    },
  });
}

export function useNewsletterIssues() {
  return useQuery({
    queryKey: ['newsletter'],
    queryFn: async () => (await req<{ data: any[] }>('/admin/newsletter')).data.map(normalizeNewsletter),
  });
}

export function useNewsletterIssue(id: number) {
  return useQuery({
    queryKey: ['newsletter', id],
    queryFn: async () => {
      const rows = (await req<{ data: any[] }>('/admin/newsletter')).data;
      const row = rows.find((r) => r.id === id);
      if (!row) throw new Error(`newsletter ${id} not found`);
      return normalizeNewsletter(row);
    },
    enabled: Number.isFinite(id),
  });
}

export function useNewsletterPreview(id: number) {
  return useQuery({
    queryKey: ['newsletter', id, 'preview'],
    queryFn: async () => {
      // Raw fetch rather than req(): the endpoint returns HTML, not JSON.
      // The session cookie still carries the auth.
      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/admin/newsletter/${id}/preview`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`preview ${res.status}`);
      return res.text();
    },
    enabled: Number.isFinite(id),
  });
}

export function useAudit(limit = 100) {
  return useQuery({
    queryKey: ['audit', { limit }],
    queryFn: async () => (await req<{ data: any[] }>(`/admin/audit?limit=${limit}`)).data.map(normalizeAudit),
    refetchInterval: 15000,
  });
}

export function useOutbox(params: { status?: string; limit?: number } = {}) {
  return useQuery({
    queryKey: ['outbox', params],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.limit) qs.set('limit', String(params.limit));
      const s = qs.toString();
      const res = await req<{ data: any[] }>(`/admin/outbox${s ? `?${s}` : ''}`);
      return res.data.map(normalizeOutbox);
    },
    refetchInterval: 15000,
  });
}
